<template>
    <div class="card">
        <DataTable
            :value="users"
            :lazy="true"
            :paginator="true"
            :first="(params.page - 1) * params.page_size"
            :rows="params.page_size"
            ref="dt"
            dataKey="id"
            :totalRecords="totalUsers"
            :loading="loading"
            @page="onPage($event)"
            responsiveLayout="scroll"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #header>
                <div class="flex">
                    <BaseTextInput v-model="email" type="text" label="User Email" placeholder="Start typing user email.." />
                </div>
            </template>
            <template #empty> No Users available. </template>
            <Column field="id" header="USER ID" />
            <Column field="name" header="NAME" />
            <Column field="surname" header="SURNAME" />
            <Column field="email" header="EMAIL" />
            <Column v-if="authStore.isGodUser" field="avoids_sso_when_active" header="BYPASS SSO" style="min-width: 8rem">
                <template #body="slotProps">
                    <span :class="slotProps.data.avoids_sso_when_active ? 'p-badge p-badge-success' : 'p-badge p-badge-secondary'">
                        {{ slotProps.data.avoids_sso_when_active ? 'Yes' : 'No' }}
                    </span>
                </template>
            </Column>
            <Column v-if="authStore.hasPermission(UserPermission.READ_USER_DEPARTMENT)" field="department" header="USER DEPARTMENT" style="max-width: 12rem">
                <template #body="slotProps">
                    <CurrentUserDepartmentBadge v-if="!loading && slotProps.data.id" :userId="slotProps.data.id" />
                </template>
            </Column>

            <Column field="locked" header="UNLOCK" style="min-width: 2rem">
                <template #body="slotProps">
                    <Button v-if="slotProps.data.locked === 1 &&  authStore.hasPermission(UserPermission.WRITE_USERS)"
                            icon="pi pi-key"  severity="help" raised rounded class=" p-button-outlined mr-2"
                            @click="unlockUserDialog(slotProps.data)" :disabled="unlockingInProgress"

                    />
                </template>
            </Column>

            <Column field="actions" header="ACTION" style="min-width: 16rem">
                <template #body="slotProps">
                    <Button v-if="authStore.hasPermission(UserPermission.WRITE_USERS)" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="editUser(slotProps.data)" />
                    <Button v-if="authStore.hasPermission(UserPermission.WRITE_USER_DEPARTMENT)" icon="pi pi-cog" class="p-button-rounded p-button-primary mr-2" @click="editUserDepartments(slotProps.data)"  />
                    <Button v-if="authStore.hasPermission(UserPermission.DELETE_USERS)" icon="pi pi-trash" class="p-button-rounded p-button-danger mr-2" @click="confirmRequest(slotProps.data, deleteUserAndRefresh)" />
                    <Button v-if="authStore.hasPermission(UserPermission.READ_PERMISSION_USER)" icon="pi pi-users" class="p-button-rounded p-button-info" @click="manageUserGroups(slotProps.data)" />
                </template>
            </Column>
        </DataTable>
        <Dialog v-model:visible="editUserDialog" :style="{ width: '450px' }" header="Edit User" :modal="true" class="p-fluid dialog-header">
            <UserForm @close-dialog="onUserFormClose" />
        </Dialog>

        <Dialog v-model:visible="departmentUserManagementDialog" :style="{ width: '450px' }" header="Manage departments" :modal="true" class="p-fluid dialog-header">
            <DepartmentUserForm :user="selectedUser" @close-department-user-dialog="closeUserDepartmentModal"/>
        </Dialog>

        <Dialog close-on-escape v-model:visible="userLockedDialogVisible" :style="{ width: '450px' }" header="User locked" :modal="true" class="p-fluid dialog-header">
            <div>
                This account has been locked due to a number of failed login attempts at
                <b>{{formatDate(currentUserDetails.locked_email_sent_at)}}</b>.
            </div>
            <br />

            <h6>
                Do you want to unlock their account?
            </h6>
            <br />
            <p>
                It's advised that the user resets their password once the account has been unlocked.
            </p>

            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="userLockedDialogVisible = false" />
                <Button icon="pi pi-key" severity="help" class="p-button-primary mr-2" label="Unlock"
                        @click="unlockUser" :disabled="unlockingInProgress" :loading="unlockingInProgress"
                />
            </template>
        </Dialog>
        <Dialog v-model:visible="userGroupsDialog" :style="{ width: '850px' }" header="Manage User Groups" :modal="true" class="p-fluid dialog-header">
            <div v-if="selectedUser">
                <div class="field col-12">
                    <label for="description">Select Groups for the User</label>
                    <br />
                    <small class="p-invalid" v-if="submitted && groupsSource[1].length <= 0">Select at least one group from the list.</small>
                    <PickList v-model="groupsSource" dataKey="id" :showSourceControls="false" :showTargetControls="false">
                        <template #sourceheader> {{ groupsSource[0].length }} Groups Available </template>
                        <template #targetheader> Added Group List </template>
                        <template #item="{ item }">ID: {{ item.id }} <label class="ml-2">{{ item.code }}</label></template>
                    </PickList>
                </div>
                <div class="field flex justify-content-center">
                    <Button label="Save" class="my-2" @click="addGroupsToUser" :loading="savingGroups"></Button>
                </div>
            </div>
        </Dialog>
    </div>
</template>

<script setup>
import CurrentUserDepartmentBadge from '../../../product/ams/modules/department/components/CurrentUserDepartmentBadge.vue';

import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { authStore, userStore, departmentStore, groupStore } from '../../../store/global-stores';
import { UserPermission } from '../../permission/enum';
import { useConfirmRemove } from '@/composables/ConfirmComposable';
import UserForm from '@/modules/user/components/UserForm.vue';
import DepartmentUserForm from '@/modules/user/components/DepartmentUserForm.vue';
import { authApi } from '@/utilities/axios';
import { showToast } from '@/utilities/toast-handler';
import { DateTime } from 'luxon';


const { confirmRequest } = useConfirmRemove();

const { params, users, totalUsers, loading, userGroups } = storeToRefs(userStore);
const { groups } = storeToRefs(groupStore);

const editUserDialog = ref(false);
const email = ref(null);

const departmentUserManagementDialog = ref(false);
const selectedUser = ref(null);

const unlockingInProgress = ref(false);

// User groups management
const userGroupsDialog = ref(false);
const groupsSource = ref([[], []]);
const submitted = ref(false);
const savingGroups = ref(false);

watch(
    () => email.value,
    () => {
        params.value.page = 1;
        loadUsers();
    }
);

onMounted(() => {
    loadUsers();
    loadDepartments();
    loadGroups();
});

onUnmounted(() => {
    userStore.initUsers();
});

const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

const userLockedDialogVisible = ref(false)
const currentUserDetails = ref({})

function unlockUserDialog(data) {
    userLockedDialogVisible.value = true
    currentUserDetails.value = data
}

function unlockUser() {
    const data = currentUserDetails.value

    if (typeof data !== 'undefined' && Object.hasOwn(data, 'id')) {
        unlockingInProgress.value = true
        const userId = data.id

        const apiUrl = '/v2/admin/users/unlock'

        // PATCH    http://localhost:8888/api/v2/admin/users/3357/unlock

        authApi.post(apiUrl, {
            company_id: company_id.value,
            users: [
                userId
            ]
        }).then((response) => {
            if (response.statusCode === 200) {
                showToast({ severity: 'info', summary: 'User unlocked' }, 'User unlocked').then(() => {
                    loadUsers()
                    unlockingInProgress.value = false
                    userLockedDialogVisible.value = false
                });
            } else {
                showToast(response, response.data.message);
                unlockingInProgress.value = false
                userLockedDialogVisible.value = false
            }
        }).catch((response) => {
            showToast(response, response.data.message);
            unlockingInProgress.value = false
            userLockedDialogVisible.value = false
        })
    }
}

function formatDate(dateString) {
    console.log('dateString', dateString)
    return dateString ? DateTime.fromISO(dateString).setLocale('en-GB').toFormat('dd-MM-yyyy at HH:mm') : '';
}

const loadDepartments = async () => {
    await departmentStore.fetchAllDepartments();
};

const onPage = async (event) => {
    params.value.page = event.page + 1;
    params.value.page_size = event.rows;
    await loadUsers();
};

const loadUsers = async () => {
    // Only filtered by email for now
    await userStore.fetchAllUsers({ ...params.value, email: email.value });
};

const editUser = (userData) => {
    userStore.editUser(userData);
    editUserDialog.value = true;
};

const deleteUserAndRefresh = async (userId) => {
    const deleted = await userStore.deleteUser(userId);
    if (deleted) {
        loadUsers();
    }
};

const onUserFormClose = (isCancel) => {
    editUserDialog.value = false;
    if (!isCancel) {
        loadUsers();
    }
};

const closeUserDepartmentModal = () => {
    departmentUserManagementDialog.value = false;
    loadUsers();
}

const editUserDepartments = (userData) => {
    departmentUserManagementDialog.value = true
    selectedUser.value = userData;
};

const loadGroups = async () => {
    // fetch all existing groups from the db
    const paginate = false;
    // silent: user may not have permission groups access; suppress 403 toast on Users page
    await groupStore.getAllGroups({}, paginate, { silent: true });
};

const manageUserGroups = async (userData) => {
    selectedUser.value = userData;
    userGroupsDialog.value = true;
    await loadUserGroup(userData.id);
};

// loads the user groupSource for the picklist component
const loadUserGroup = async (userId) => {
    // first load all groups into the groupsSource
    groupsSource.value[0] = [...groups.value];
    // fetch the permission group which user has access
    await userStore.fetchUserPermissionGroups(userId);
    /** After loading the user existing groups load them accordingly*/
    groupsSource.value[1] = [...userGroups.value];
    // now filter them from groupSource.value[0]
    groupsSource.value[0] = filterExistingUserGroups();
};

// this function filters out the list of array that already exists in second array from first array
const filterExistingUserGroups = () => {
    let arr1 = groupsSource.value[0];
    let arr2 = groupsSource.value[1];
    return arr1.filter((obj1) => !arr2.find((obj2) => obj1.id === obj2.id));
};

const addGroupsToUser = async () => {
    submitted.value = true;
    if (isValid()) {
        savingGroups.value = true;
        const groupIds = groupsSource.value[1]?.map((group) => group.id);
        const updated = await userStore.setUserPermissionGroups(selectedUser.value.id, groupIds);
        if (updated) {
            showToast({ severity: 'success', summary: 'Success', detail: 'User groups updated successfully' }, 'User groups updated');
            userGroupsDialog.value = false;
            selectedUser.value = null;
            groupsSource.value = [[], []];
            submitted.value = false;
        }
        savingGroups.value = false;
    }
};

const isValid = () => {
    if (!selectedUser.value || groupsSource.value[1]?.length <= 0) {
        return false;
    }
    return true;
};
</script>
